import {ActionContext, ActionTree} from 'vuex';
import {RootState} from '@/store';
import {ConversationsState} from '@/store/modules/Conversations/state';
import {_axios} from '@/plugins/axios';
import router from '@/plugins/router';
import {AxiosResponse} from 'axios';
import {AdvertConversationAPI} from '#/Advert';
import {
    Conversation,
    LastMessageConversation,
    MessageCreation,
    MessageCreationReturn,
} from '#/Conversations';

export const actions: ActionTree<ConversationsState, RootState> = {
    deleteConversation: (
        {dispatch}: ActionContext<ConversationsState, RootState>,
        {userId, advertId}: { userId: number, advertId: number },
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.delete(`conversations/${advertId}/${userId}/`)
                  .then(() => {
                      router.push({name: 'MessageList'})
                            .then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * Sur la page d'accueil des conversations, on charge toutes les annonces où l'on a commencé une conversation.
     */
    loadAdvertsConversation: ({commit, dispatch}: ActionContext<ConversationsState, RootState>): Promise<void> => {
        return new Promise(resolve => {
            _axios.get(`conversations/`)
                  .then((response: AxiosResponse<AdvertConversationAPI[]>) => {
                      commit('GET_ADVERTS_CONVERSATION', response.data);
                      resolve();
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    loadConversation: (
        {commit, dispatch, rootGetters}: ActionContext<ConversationsState, RootState>,
        {userId, advertId}: { userId: number, advertId: number },
    ): Promise<void> => {
        return new Promise(resolve => {
            _axios.get(`conversations/${advertId}/${userId}/`)
                  .then((response: AxiosResponse<Conversation>) => {
                      const {advert, messages} = response.data;
                      commit('GET_CONVERSATION', {
                          advert,
                          messages: messages.map(message => {
                              const {readAt, sentAt, ...restMessage} = message;
                              return {
                                  ...restMessage,
                                  readAt: readAt ? new Date(readAt) : null,
                                  sentAt: new Date(sentAt),
                              };
                          }),
                      });
                      Promise.all([
                          dispatch('markReadConversation', {userId, advertId}),
                          dispatch('messageNotReadNumber'),
                      ])
                             .then(() => {
                                 resolve();
                             });
                  })
                  .catch(error => {
                      if (error.response && error.response.status === 404) {
                          dispatch('loadAdvert', advertId)
                              .then(() => {
                                  commit('GET_CONVERSATION', {
                                      advert: rootGetters.advert,
                                      messages: [],
                                  });
                                  resolve();
                              })
                              .catch((e) => {
                                  dispatch('displayError', e.response, {root: true})
                                      .then(() => {
                                          resolve();
                                      });
                              });
                      } else {
                          dispatch('displayError', error.response, {root: true})
                              .then(() => {
                                  resolve();
                              });
                      }
                  });
        });
    },

    loadConversations: (
        {commit, dispatch}: ActionContext<ConversationsState, RootState>,
        id: number,
    ): Promise<void> => {
        return new Promise(resolve => {
            _axios.get(`conversations/${id}/`)
                  .then((response: AxiosResponse<LastMessageConversation[]>) => {
                      commit('GET_CONVERSATIONS', {
                          id: `${id}`,
                          conversations: response.data.map(({user, lastMessage}) => ({
                              user,
                              lastMessage: {
                                  ...lastMessage,
                                  sentAt: new Date(lastMessage.sentAt),
                              },
                          })),
                      });
                      resolve();
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    markReadConversation: (
        context: ActionContext<ConversationsState, RootState>,
        {userId, advertId}: { userId: number, advertId: number },
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.put(`conversations/${advertId}/${userId}/mark-read/`)
                  .then(() => {
                      resolve();
                  })
                  .catch(() => {
                      reject();
                  });
        });
    },

    messageNotReadNumber: ({commit, dispatch}: ActionContext<ConversationsState, RootState>): Promise<void> => {
        return new Promise(resolve => {
            _axios.get('notifications/unread/count/')
                  .then((response: AxiosResponse<{numberOfMessages: number}>) => {
                      commit('HAVE_A_NEW_MESSAGE', response.data.numberOfMessages);
                      resolve();
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * Envoie un nouveau message.
     */
    sendMessage: (
        {commit, dispatch, rootGetters}: ActionContext<ConversationsState, RootState>,
        message: MessageCreation,
    ): Promise<void> => {
        return new Promise(resolve => {
            _axios.post(`conversations/message/`, message)
                  .then((response: AxiosResponse<MessageCreationReturn>) => {
                      const {sentAt, ...rest} = response.data;
                      commit('ADD_NEW_MESSAGE', {
                          ...rest,
                          sentAt: new Date(sentAt),
                          author: rootGetters.profile,
                      });
                      resolve();
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },
};
